import type { TeleSession } from '@shareview/tele-engine-core';
import {
  createFetchTelemetrySender,
  createTelemetryCollector,
  flushOnPageHide,
  type TelemetryCollector,
} from '@shareview/tele-engine-telemetry';
import { appConfig } from './config';

/** The page's telemetry collector plus a tiny status store for the header. */
export interface PocTelemetry {
  collector: TelemetryCollector;
  status: () => string;
  subscribe: (listener: () => void) => () => void;
  dispose: () => Promise<void>;
}

/**
 * Posts schema v1 telemetry for the session to the Calls API with the signed-in user's token.
 * `getAccessToken` returns the token kept in memory: the unload flush must start inside the
 * pagehide handler and cannot await a refresh.
 */
export function createPocTelemetry(session: TeleSession, getAccessToken: () => string | null): PocTelemetry {
  let status = 'telemetry: nothing sent yet';
  const listeners = new Set<() => void>();
  const sender = createFetchTelemetrySender({ apiBaseUrl: appConfig.apiBaseUrl, getAccessToken });
  const collector = createTelemetryCollector(session, {
    client: {
      engineVersion: __TELE_ENGINE_VERSION__,
      platform: 'web',
      browser: navigator.userAgent.match(/(Chrome|Firefox|Version)\/[\d.]+/)?.[0],
      os: navigator.platform || undefined,
      networkType: networkType(),
    },
    sender: {
      send: async (callId, batch, options) => {
        const result = await sender.send(callId, batch, options);
        status = `telemetry ${result.status}: ${batch.windows.length} windows, ${batch.events.map((e) => e.type).join(', ') || 'no events'}`;
        listeners.forEach((l) => l());
        return result;
      },
    },
  });
  const detach = flushOnPageHide(collector);
  return {
    collector,
    status: () => status,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose: async () => {
      detach();
      await collector.stop();
    },
  };
}

function networkType(): 'wifi' | 'ethernet' | 'cellular' | 'unknown' {
  const type = (navigator as { connection?: { type?: string } }).connection?.type;
  return type === 'wifi' || type === 'ethernet' || type === 'cellular' ? type : 'unknown';
}
